import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  QrCode2 as QrCodeIcon,
  Smartphone as SmartphoneIcon,
  Verified as VerifiedIcon,
  Key as KeyIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';

interface TwoFactorSetupProps {
  onComplete: () => void;
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const generateSecret = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/api/auth/2fa/generate', {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate 2FA secret');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/api/auth/2fa/verify', { token }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setBackupCodes(response.data.backupCodes);
      setActiveStep(2);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to verify token');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const content = `BACKUP CODES FOR YOUR CONTRACT AI ACCOUNT\n\n${backupCodes.join('\n')}\n\nKeep these codes safe and secure. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contract-ai-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const finishSetup = () => {
    onComplete();
  };

  const steps = ['Generate Secret', 'Verify Code', 'Save Backup Codes'];

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" gutterBottom>
              Two-factor authentication adds an extra layer of security to your account. 
              Once enabled, you'll need to provide a verification code from your authenticator app 
              in addition to your password when signing in.
            </Typography>
            
            <Typography variant="body1" sx={{ mt: 2, mb: 4 }}>
              Click the button below to start the setup process.
            </Typography>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={generateSecret}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <QrCodeIcon />}
              sx={{ py: 1.5, px: 3 }}
            >
              Generate QR Code
            </Button>
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <QrCodeIcon sx={{ mr: 1 }} /> Scan QR Code
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </Typography>
                    
                    {qrCode && (
                      <Box sx={{ textAlign: 'center', my: 3 }}>
                        <img src={qrCode} alt="QR Code" style={{ maxWidth: '100%' }} />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <KeyIcon sx={{ mr: 1 }} /> Manual Setup
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      If you can't scan the QR code, enter this code manually in your authenticator app:
                    </Typography>
                    
                    {secret && (
                      <Box sx={{ 
                        my: 3, 
                        p: 2, 
                        backgroundColor: 'grey.100', 
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '1.2rem',
                        textAlign: 'center',
                        wordBreak: 'break-all'
                      }}>
                        {secret}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <VerifiedIcon sx={{ mr: 1 }} /> Verify Setup
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Enter the verification code from your authenticator app to complete the setup:
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Verification Code"
                      variant="outlined"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="Enter 6-digit code"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                      sx={{ mb: 3 }}
                    />
                    
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={verifyAndEnable}
                      disabled={loading || token.length < 6}
                      startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                      sx={{ py: 1.5, px: 3 }}
                    >
                      Verify and Enable 2FA
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ mt: 4 }}>
            <Alert severity="success" sx={{ mb: 4 }}>
              <Typography variant="subtitle1">
                Two-factor authentication has been successfully enabled!
              </Typography>
            </Alert>
            
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <KeyIcon sx={{ mr: 1 }} /> Backup Codes
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  Save these backup codes in a secure place. You can use these codes to sign in if you lose access to your authenticator app.
                  Each code can only be used once.
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  {backupCodes.map((code, index) => (
                    <Grid item xs={6} sm={4} key={index}>
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: 'grey.100', 
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        textAlign: 'center'
                      }}>
                        {code}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={downloadBackupCodes}
                    startIcon={<DownloadIcon />}
                    sx={{ mr: 2 }}
                  >
                    Download Backup Codes
                  </Button>
                  
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={finishSetup}
                    startIcon={<CheckCircleIcon />}
                  >
                    Finish Setup
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Set Up Two-Factor Authentication
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      
      <Stepper activeStep={activeStep} sx={{ my: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {renderStepContent(activeStep)}
    </Paper>
  );
};

export default TwoFactorSetup; 